/****************************************************************************
 Copyright (c) 2017-2018 Xiamen Yaji Software Co., Ltd.

 https://www.cocos.com/

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated engine source code (the "Software"), a limited,
 worldwide, royalty-free, non-assignable, revocable and non-exclusive license
 to use Cocos Creator solely to develop games on your target platforms. You shall
 not use Cocos Creator software for developing other software or tools that's
 used for developing games. You are not granted to publish, distribute,
 sublicense, and/or sell copies of Cocos Creator.

 The software or tools in this License Agreement are licensed, not sold.
 Xiamen Yaji Software Co., Ltd. reserves all rights not expressly granted to you.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 THE SOFTWARE.
 ****************************************************************************/


const VFOneColor = cc.gfx.VertexFormat.XY_UV_Color;
const VFTwoColor = cc.gfx.VertexFormat.XY_UV_Two_Color;
const gfx = cc.gfx;

const FLAG_BATCH = 0x10;
const FLAG_TWO_COLOR = 0x01;

const { ccclass, property, executeInEditMode } = cc._decorator;


function _spineColorToInt32(spineColor) {
    return ((spineColor.a << 24) >>> 0) + (spineColor.b << 16) + (spineColor.g << 8) + spineColor.r;
}

@executeInEditMode
export default class SpineAssembler extends cc.Assembler {
    _getSlotMaterial(tex, blendMode) {
        let src, dst;
        switch (blendMode) {
            case sp.spine.BlendMode.Additive:
                src = this._premultipliedAlpha ? cc.macro.ONE : cc.macro.SRC_ALPHA;
                dst = cc.macro.ONE;
                break;
            case sp.spine.BlendMode.Multiply:
                src = cc.macro.DST_COLOR;
                dst = cc.macro.ONE_MINUS_SRC_ALPHA;
                break;
            case sp.spine.BlendMode.Screen:
                src = cc.macro.ONE;
                dst = cc.macro.ONE_MINUS_SRC_COLOR;
                break;
            case sp.spine.BlendMode.Normal:
            default:
                src = this._premultipliedAlpha ? cc.macro.ONE : cc.macro.SRC_ALPHA;
                dst = cc.macro.ONE_MINUS_SRC_ALPHA;
                break;
        }

        let useModel = !this._comp.enableBatch;
        let baseMaterial = this._comp._materials[0];
        if (!baseMaterial) return null;

        // The key use to find corresponding material
        let key = tex.getId() + src + dst + this._useTint + useModel;
        let materialCache = this._comp._materialCache;
        let material = materialCache[key];
        if (!material) {
            if (!materialCache.baseMaterial) {
                material = baseMaterial;
                materialCache.baseMaterial = baseMaterial;
            } else {
                material = cc.MaterialVariant.create(baseMaterial);
            }

            material.define('CC_USE_MODEL', useModel);
            material.define('USE_TINT', this._useTint);
            // update texture
            material.setProperty('texture', tex);

            // update blend function
            material.setBlend(
                true,
                gfx.BLEND_FUNC_ADD,
                src, dst,
                gfx.BLEND_FUNC_ADD,
                src, dst
            );
            materialCache[key] = material;
        }
        return material;
    }

    _handleColor(color) {
        // temp rgb has multiply 255, so need divide 255;
        this._fa = color.fa * this._nodeA;
        this._multiplier = this._premultipliedAlpha ? this._fa / 255 : 1;
        this._r = this._nodeR * this._multiplier;
        this._g = this._nodeG * this._multiplier;
        this._b = this._nodeB * this._multiplier;

        this._fr = color.fr * this._r;
        this._fg = color.fg * this._g;
        this._fb = color.fb * this._b;
        this._finalColor32 = ((this._fa << 24) >>> 0) + (this._fb << 16) + (this._fg << 8) + this._fr;

        this._dr = color.dr * this._r;
        this._dg = color.dg * this._g;
        this._db = color.db * this._b;
        this._da = this._premultipliedAlpha ? 255 : 0;
        this._darkColor32 = ((this._da << 24) >>> 0) + (this._db << 16) + (this._dg << 8) + this._dr;
    }

    _handleVal = 0x00;
    _quadTriangles = [0, 1, 2, 2, 3, 0];
    _slotColor = cc.color(0, 0, 255, 255);
    _boneColor = cc.color(255, 0, 0, 255);
    _originColor = cc.color(0, 255, 0, 255);
    _meshColor = cc.color(255, 255, 0, 255);

    _finalColor = null;
    _darkColor = null;
    _tempPos = null
    _tempUv = null;

    _premultipliedAlpha;
    _multiplier;
    _slotRangeStart;
    _slotRangeEnd;
    _useTint;
    _debugSlots;
    _debugBones;
    _debugMesh;
    _nodeR
    _nodeG
    _nodeB
    _nodeA;
    _finalColor32
    _darkColor32;
    _vertexFormat;
    _perVertexSize;
    _perClipVertexSize;

    _vertexFloatCount = 0
    _vertexCount = 0
    _vertexFloatOffset = 0
    _vertexOffset = 0
    _indexCount = 0
    _indexOffset = 0
    _vfOffset = 0;
    _tempr
    _tempg
    _tempb;
    _inRange;
    _mustFlush;

    _x
    _y
    _m00
    _m04
    _m12
    _m01
    _m05
    _m13;
    _r
    _g
    _b

    _fr
    _fg
    _fb
    _fa
    _dr
    _dg
    _db
    _da;

    _comp
    _buffer
    _renderer
    _node
    _needColor
    _vertexEffect;


    updateRenderData(comp) {
        if (comp.isAnimationCached()) return;
        let skeleton = comp._skeleton;
        if (skeleton) {
            skeleton.updateWorldTransform();
        }
    }

    fillVertices(skeletonColor, attachmentColor, slotColor, clipper, slot) {
        if (!CC_NATIVERENDERER) {
            this._finalColor = new sp.spine.Color(1, 1, 1, 1);
            this._darkColor = new sp.spine.Color(1, 1, 1, 1);
            this._tempPos = new sp.spine.Vector2();
            this._tempUv = new sp.spine.Vector2();
        }

        let vbuf = this._buffer._vData,
            ibuf = this._buffer._iData,
            uintVData = this._buffer._uintVData;
        let offsetInfo;

        this._finalColor.a = slotColor.a * attachmentColor.a * skeletonColor.a * this._nodeA * 255;
        this._multiplier = this._premultipliedAlpha ? this._finalColor.a : 255;
        this._tempr = this._nodeR * attachmentColor.r * skeletonColor.r * this._multiplier;
        this._tempg = this._nodeG * attachmentColor.g * skeletonColor.g * this._multiplier;
        this._tempb = this._nodeB * attachmentColor.b * skeletonColor.b * this._multiplier;

        this._finalColor.r = this._tempr * slotColor.r;
        this._finalColor.g = this._tempg * slotColor.g;
        this._finalColor.b = this._tempb * slotColor.b;

        if (slot.darkColor == null) {
            this._darkColor.set(0.0, 0.0, 0.0, 1.0);
        } else {
            this._darkColor.r = slot.darkColor.r * this._tempr;
            this._darkColor.g = slot.darkColor.g * this._tempg;
            this._darkColor.b = slot.darkColor.b * this._tempb;
        }
        this._darkColor.a = this._premultipliedAlpha ? 255 : 0;

        if (!clipper.isClipping()) {
            if (this._vertexEffect) {
                for (let v = this._vertexFloatOffset, n = this._vertexFloatOffset + this._vertexFloatCount; v < n; v += this._perVertexSize) {
                    this._tempPos.x = vbuf[v];
                    this._tempPos.y = vbuf[v + 1];
                    this._tempUv.x = vbuf[v + 2];
                    this._tempUv.y = vbuf[v + 3];
                    this._vertexEffect.transform(this._tempPos, this._tempUv, this._finalColor, this._darkColor);

                    vbuf[v] = this._tempPos.x;        // x
                    vbuf[v + 1] = this._tempPos.y;        // y
                    vbuf[v + 2] = this._tempUv.x;         // u
                    vbuf[v + 3] = this._tempUv.y;         // v
                    uintVData[v + 4] = _spineColorToInt32(this._finalColor);                  // light color
                    this._useTint && (uintVData[v + 5] = _spineColorToInt32(this._darkColor));      // dark color
                }
            } else {
                this._finalColor32 = _spineColorToInt32(this._finalColor);
                this._darkColor32 = _spineColorToInt32(this._darkColor);

                for (let v = this._vertexFloatOffset, n = this._vertexFloatOffset + this._vertexFloatCount; v < n; v += this._perVertexSize) {
                    uintVData[v + 4] = this._finalColor32;                   // light color
                    this._useTint && (uintVData[v + 5] = this._darkColor32);      // dark color
                }
            }
        } else {
            let uvs = vbuf.subarray(this._vertexFloatOffset + 2);
            clipper.clipTriangles(vbuf.subarray(this._vertexFloatOffset), this._vertexFloatCount, ibuf.subarray(this._indexOffset), this._indexCount, uvs, this._finalColor, this._darkColor, this._useTint, this._perVertexSize);
            let clippedVertices = new Float32Array(clipper.clippedVertices);
            let clippedTriangles = clipper.clippedTriangles;

            // insure capacity
            this._indexCount = clippedTriangles.length;
            this._vertexFloatCount = clippedVertices.length / this._perClipVertexSize * this._perVertexSize;

            offsetInfo = this._buffer.request(this._vertexFloatCount / this._perVertexSize, this._indexCount);
            this._indexOffset = offsetInfo.indiceOffset,
                this._vertexOffset = offsetInfo.vertexOffset,
                this._vertexFloatOffset = offsetInfo.byteOffset >> 2;
            vbuf = this._buffer._vData,
                ibuf = this._buffer._iData;
            uintVData = this._buffer._uintVData;

            // fill indices
            ibuf.set(clippedTriangles, this._indexOffset);

            // fill vertices contain x y u v light color dark color
            if (this._vertexEffect) {
                for (let v = 0, n = clippedVertices.length, offset = this._vertexFloatOffset; v < n; v += this._perClipVertexSize, offset += this._perVertexSize) {
                    this._tempPos.x = clippedVertices[v];
                    this._tempPos.y = clippedVertices[v + 1];
                    this._finalColor.set(clippedVertices[v + 2], clippedVertices[v + 3], clippedVertices[v + 4], clippedVertices[v + 5]);
                    this._tempUv.x = clippedVertices[v + 6];
                    this._tempUv.y = clippedVertices[v + 7];
                    if (this._useTint) {
                        this._darkColor.set(clippedVertices[v + 8], clippedVertices[v + 9], clippedVertices[v + 10], clippedVertices[v + 11]);
                    } else {
                        this._darkColor.set(0, 0, 0, 0);
                    }
                    this._vertexEffect.transform(this._tempPos, this._tempUv, this._finalColor, this._darkColor);

                    vbuf[offset] = this._tempPos.x;             // x
                    vbuf[offset + 1] = this._tempPos.y;         // y
                    vbuf[offset + 2] = this._tempUv.x;          // u
                    vbuf[offset + 3] = this._tempUv.y;          // v
                    uintVData[offset + 4] = _spineColorToInt32(this._finalColor);
                    if (this._useTint) {
                        uintVData[offset + 5] = _spineColorToInt32(this._darkColor);
                    }
                }
            } else {
                for (let v = 0, n = clippedVertices.length, offset = this._vertexFloatOffset; v < n; v += this._perClipVertexSize, offset += this._perVertexSize) {
                    vbuf[offset] = clippedVertices[v];         // x
                    vbuf[offset + 1] = clippedVertices[v + 1];     // y
                    vbuf[offset + 2] = clippedVertices[v + 6];     // u
                    vbuf[offset + 3] = clippedVertices[v + 7];     // v

                    this._finalColor32 = ((clippedVertices[v + 5] << 24) >>> 0) + (clippedVertices[v + 4] << 16) + (clippedVertices[v + 3] << 8) + clippedVertices[v + 2];
                    uintVData[offset + 4] = this._finalColor32;

                    if (this._useTint) {
                        this._darkColor32 = ((clippedVertices[v + 11] << 24) >>> 0) + (clippedVertices[v + 10] << 16) + (clippedVertices[v + 9] << 8) + clippedVertices[v + 8];
                        uintVData[offset + 5] = this._darkColor32;
                    }
                }
            }
        }
    }

    realTimeTraverse(worldMat) {
        let vbuf;
        let ibuf;

        let locSkeleton = this._comp._skeleton;
        let skeletonColor = locSkeleton.color;
        let graphics = this._comp._debugRenderer;
        let clipper = this._comp._clipper;
        let material = null;
        let attachment, attachmentColor, slotColor, uvs, triangles;
        let isRegion, isMesh, isClip;
        let offsetInfo;
        let slot;
        let worldMatm;

        this._slotRangeStart = this._comp._startSlotIndex;
        this._slotRangeEnd = this._comp._endSlotIndex;
        this._inRange = false;
        if (this._slotRangeStart == -1) this._inRange = true;

        this._debugSlots = this._comp.debugSlots;
        this._debugBones = this._comp.debugBones;
        this._debugMesh = this._comp.debugMesh;
        if (graphics && (this._debugBones || this._debugSlots || this._debugMesh)) {
            graphics.clear();
            graphics.lineWidth = 2;
        }

        // x y u v r1 g1 b1 a1 r2 g2 b2 a2 or x y u v r g b a 
        this._perClipVertexSize = this._useTint ? 12 : 8;

        this._vertexFloatCount = 0;
        this._vertexFloatOffset = 0;
        this._vertexOffset = 0;
        this._indexCount = 0;
        this._indexOffset = 0;

        let _followSlotMap = this._comp._followSlotMap || {}
        for (let slotName in _followSlotMap) {
            let followSlot = _followSlotMap[slotName];
            followSlot._renderFlag |= cc.RenderFlow.FLAG_BREAK_FLOW;
        }
        for (let slotIdx = 0, slotCount = locSkeleton.drawOrder.length; slotIdx < slotCount; slotIdx++) {
            slot = locSkeleton.drawOrder[slotIdx];

            if (slot == undefined || !slot.bone.active) {
                continue;
            }

            if (this._slotRangeStart >= 0 && this._slotRangeStart == slot.data.index) {
                this._inRange = true;
            }

            if (!this._inRange) {
                clipper.clipEndWithSlot(slot);
                continue;
            }

            if (this._slotRangeEnd >= 0 && this._slotRangeEnd == slot.data.index) {
                this._inRange = false;
            }

            this._vertexFloatCount = 0;
            this._indexCount = 0;

            attachment = slot.getAttachment();
            if (!attachment) {
                clipper.clipEndWithSlot(slot);
                continue;
            }

            isRegion = attachment instanceof spine.RegionAttachment;
            isMesh = attachment instanceof spine.MeshAttachment;
            isClip = attachment instanceof spine.ClippingAttachment;

            if (isClip) {
                clipper.clipStart(slot, attachment);
                continue;
            }

            if (!isRegion && !isMesh) {
                clipper.clipEndWithSlot(slot);
                continue;
            }
            let _followSlotMap = this._comp._followSlotMap || {}
            let followSlot = _followSlotMap[slot.data.name];
            if (followSlot) {
                this._renderer._flush();

                this._renderer.worldMatDirty++;
                followSlot._updateLocalMatrix();

                let tempMat4 = new cc.Mat4();
                let tm = tempMat4.m;
                tm[0] = slot.bone.a;
                tm[1] = slot.bone.c;
                tm[4] = slot.bone.b;
                tm[5] = slot.bone.d;
                tm[12] = slot.bone.worldX;
                tm[13] = slot.bone.worldY;
                cc.Mat4.mul(tempMat4, tempMat4, followSlot._matrix);
                cc.Mat4.mul(followSlot._worldMatrix, this._node._worldMatrix, tempMat4);

                let a = slot.color.a * attachment.color.a * skeletonColor.a * this._nodeA * 255;
                followSlot.opacity = a;
                let colorVal = followSlot._color._val;
                followSlot._color._fastSetA(a);

                followSlot._renderFlag &= ~(cc.RenderFlow.FLAG_TRANSFORM | cc.RenderFlow.FLAG_BREAK_FLOW);
                cc.RenderFlow.visitRootNode(followSlot);
                followSlot._renderFlag |= cc.RenderFlow.FLAG_BREAK_FLOW;

                followSlot._color._fastSetA(colorVal);

                this._renderer.worldMatDirty--;
                this._renderer._flush();
                this._renderer.node = this._comp.node;

                continue;
            }
            material = this._getSlotMaterial(attachment.region.texture._texture, slot.data.blendMode);
            if (!material) {
                clipper.clipEndWithSlot(slot);
                continue;
            }

            if (this._mustFlush || material.getHash() !== this._renderer.material.getHash()) {
                this._mustFlush = false;
                this._renderer._flush();
                this._renderer.node = this._node;
                this._renderer.material = material;
            }

            if (isRegion) {

                triangles = this._quadTriangles;

                // insure capacity
                this._vertexFloatCount = 4 * this._perVertexSize;
                this._indexCount = 6;

                offsetInfo = this._buffer.request(4, 6);
                this._indexOffset = offsetInfo.indiceOffset,
                    this._vertexOffset = offsetInfo.vertexOffset,
                    this._vertexFloatOffset = offsetInfo.byteOffset >> 2;
                vbuf = this._buffer._vData,
                    ibuf = this._buffer._iData;

                // compute vertex and fill x y
                attachment.computeWorldVertices(slot.bone, vbuf, this._vertexFloatOffset, this._perVertexSize);

                // draw debug slots if enabled graphics
                if (graphics && this._debugSlots) {
                    graphics.strokeColor = this._slotColor;
                    graphics.moveTo(vbuf[this._vertexFloatOffset], vbuf[this._vertexFloatOffset + 1]);
                    for (let ii = this._vertexFloatOffset + this._perVertexSize, nn = this._vertexFloatOffset + this._vertexFloatCount; ii < nn; ii += this._perVertexSize) {
                        graphics.lineTo(vbuf[ii], vbuf[ii + 1]);
                    }
                    graphics.close();
                    graphics.stroke();
                }
            }
            else if (isMesh) {

                triangles = attachment.triangles;

                // insure capacity
                this._vertexFloatCount = (attachment.worldVerticesLength >> 1) * this._perVertexSize;
                this._indexCount = triangles.length;

                offsetInfo = this._buffer.request(this._vertexFloatCount / this._perVertexSize, this._indexCount);
                this._indexOffset = offsetInfo.indiceOffset,
                    this._vertexOffset = offsetInfo.vertexOffset,
                    this._vertexFloatOffset = offsetInfo.byteOffset >> 2;
                vbuf = this._buffer._vData,
                    ibuf = this._buffer._iData;

                // compute vertex and fill x y
                attachment.computeWorldVertices(slot, 0, attachment.worldVerticesLength, vbuf, this._vertexFloatOffset, this._perVertexSize);

                // draw debug mesh if enabled graphics
                if (graphics && this._debugMesh) {
                    graphics.strokeColor = this._meshColor;

                    for (let ii = 0, nn = triangles.length; ii < nn; ii += 3) {
                        let v1 = triangles[ii] * this._perVertexSize + this._vertexFloatOffset;
                        let v2 = triangles[ii + 1] * this._perVertexSize + this._vertexFloatOffset;
                        let v3 = triangles[ii + 2] * this._perVertexSize + this._vertexFloatOffset;

                        graphics.moveTo(vbuf[v1], vbuf[v1 + 1]);
                        graphics.lineTo(vbuf[v2], vbuf[v2 + 1]);
                        graphics.lineTo(vbuf[v3], vbuf[v3 + 1]);
                        graphics.close();
                        graphics.stroke();
                    }
                }
            }

            if (this._vertexFloatCount == 0 || this._indexCount == 0) {
                clipper.clipEndWithSlot(slot);
                continue;
            }

            // fill indices
            ibuf.set(triangles, this._indexOffset);

            // fill u v
            uvs = attachment.uvs;
            for (let v = this._vertexFloatOffset, n = this._vertexFloatOffset + this._vertexFloatCount, u = 0; v < n; v += this._perVertexSize, u += 2) {
                vbuf[v + 2] = uvs[u];           // u
                vbuf[v + 3] = uvs[u + 1];       // v
            }

            attachmentColor = attachment.color,
                slotColor = slot.color;

            this.fillVertices(skeletonColor, attachmentColor, slotColor, clipper, slot);

            // reset buffer pointer, because clipper maybe realloc a new buffer in file Vertices function.
            vbuf = this._buffer._vData,
                ibuf = this._buffer._iData;

            if (this._indexCount > 0) {
                for (let ii = this._indexOffset, nn = this._indexOffset + this._indexCount; ii < nn; ii++) {
                    ibuf[ii] += this._vertexOffset;
                }

                if (worldMat) {
                    worldMatm = worldMat.m;
                    this._m00 = worldMatm[0];
                    this._m04 = worldMatm[4];
                    this._m12 = worldMatm[12];
                    this._m01 = worldMatm[1];
                    this._m05 = worldMatm[5];
                    this._m13 = worldMatm[13];
                    for (let ii = this._vertexFloatOffset, nn = this._vertexFloatOffset + this._vertexFloatCount; ii < nn; ii += this._perVertexSize) {
                        this._x = vbuf[ii];
                        this._y = vbuf[ii + 1];
                        vbuf[ii] = this._x * this._m00 + this._y * this._m04 + this._m12;
                        vbuf[ii + 1] = this._x * this._m01 + this._y * this._m05 + this._m13;
                    }
                }
                this._buffer.adjust(this._vertexFloatCount / this._perVertexSize, this._indexCount);
            }

            clipper.clipEndWithSlot(slot);
        }

        clipper.clipEnd();

        if (graphics && this._debugBones) {
            let bone;
            graphics.strokeColor = this._boneColor;
            graphics.fillColor = this._slotColor; // Root bone color is same as slot color.

            for (let i = 0, n = locSkeleton.bones.length; i < n; i++) {
                bone = locSkeleton.bones[i];
                let x = bone.data.length * bone.a + bone.worldX;
                let y = bone.data.length * bone.c + bone.worldY;

                // Bone lengths.
                graphics.moveTo(bone.worldX, bone.worldY);
                graphics.lineTo(x, y);
                graphics.stroke();

                // Bone origins.
                graphics.circle(bone.worldX, bone.worldY, Math.PI * 1.5);
                graphics.fill();
                if (i === 0) {
                    graphics.fillColor = this._originColor;
                }
            }
        }
    }

    cacheTraverse(worldMat) {

        let frame = this._comp._curFrame;
        if (!frame) return;

        let segments = frame.segments;
        if (segments.length == 0) return;

        let vbuf, ibuf, uintbuf;
        let material;
        let offsetInfo;
        let vertices = frame.vertices;
        let indices = frame.indices;
        let worldMatm;

        let frameVFOffset = 0, frameIndexOffset = 0, segVFCount = 0;
        if (worldMat) {
            worldMatm = worldMat.m;
            this._m00 = worldMatm[0];
            this._m01 = worldMatm[1];
            this._m04 = worldMatm[4];
            this._m05 = worldMatm[5];
            this._m12 = worldMatm[12];
            this._m13 = worldMatm[13];
        }

        let justTranslate = this._m00 === 1 && this._m01 === 0 && this._m04 === 0 && this._m05 === 1;
        let needBatch = (this._handleVal & FLAG_BATCH);
        let calcTranslate = needBatch && justTranslate;

        let colorOffset = 0;
        let colors = frame.colors;
        let nowColor = colors[colorOffset++];
        let maxVFOffset = nowColor.vfOffset;
        this._handleColor(nowColor);

        for (let i = 0, n = segments.length; i < n; i++) {
            let segInfo = segments[i];
            material = this._getSlotMaterial(segInfo.tex, segInfo.blendMode);
            if (!material) continue;

            if (this._mustFlush || material.getHash() !== this._renderer.material.getHash()) {
                this._mustFlush = false;
                this._renderer._flush();
                this._renderer.node = this._node;
                this._renderer.material = material;
            }

            this._vertexCount = segInfo.vertexCount;
            this._indexCount = segInfo.indexCount;

            offsetInfo = this._buffer.request(this._vertexCount, this._indexCount);
            this._indexOffset = offsetInfo.indiceOffset;
            this._vertexOffset = offsetInfo.vertexOffset;
            this._vfOffset = offsetInfo.byteOffset >> 2;
            vbuf = this._buffer._vData;
            ibuf = this._buffer._iData;
            uintbuf = this._buffer._uintVData;

            for (let ii = this._indexOffset, il = this._indexOffset + this._indexCount; ii < il; ii++) {
                ibuf[ii] = this._vertexOffset + indices[frameIndexOffset++];
            }

            segVFCount = segInfo.vfCount;
            vbuf.set(vertices.subarray(frameVFOffset, frameVFOffset + segVFCount), this._vfOffset);
            frameVFOffset += segVFCount;

            if (calcTranslate) {
                for (let ii = this._vfOffset, il = this._vfOffset + segVFCount; ii < il; ii += 6) {
                    vbuf[ii] += this._m12;
                    vbuf[ii + 1] += this._m13;
                }
            } else if (needBatch) {
                for (let ii = this._vfOffset, il = this._vfOffset + segVFCount; ii < il; ii += 6) {
                    this._x = vbuf[ii];
                    this._y = vbuf[ii + 1];
                    vbuf[ii] = this._x * this._m00 + this._y * this._m04 + this._m12;
                    vbuf[ii + 1] = this._x * this._m01 + this._y * this._m05 + this._m13;
                }
            }

            this._buffer.adjust(this._vertexCount, this._indexCount);
            if (!this._needColor) continue;

            // handle color
            let frameColorOffset = frameVFOffset - segVFCount;
            for (let ii = this._vfOffset + 4, il = this._vfOffset + 4 + segVFCount; ii < il; ii += 6, frameColorOffset += 6) {
                if (frameColorOffset >= maxVFOffset) {
                    nowColor = colors[colorOffset++];
                    this._handleColor(nowColor);
                    maxVFOffset = nowColor.vfOffset;
                }
                uintbuf[ii] = this._finalColor32;
                uintbuf[ii + 1] = this._darkColor32;
            }
        }
    }

    fillBuffers(comp, renderer) {

        let node = comp.node;
        node._renderFlag |= cc.RenderFlow.FLAG_UPDATE_RENDER_DATA;
        if (!comp._skeleton) return;

        let nodeColor = node._color;
        this._nodeR = nodeColor.r / 255;
        this._nodeG = nodeColor.g / 255;
        this._nodeB = nodeColor.b / 255;
        this._nodeA = nodeColor.a / 255;

        this._useTint = comp.useTint || comp.isAnimationCached();
        this._vertexFormat = this._useTint ? VFTwoColor : VFOneColor;
        // x y u v color1 color2 or x y u v color
        this._perVertexSize = this._useTint ? 6 : 5;

        this._node = comp.node;
        this._buffer = renderer.getBuffer('spine', this._vertexFormat);
        this._renderer = renderer;
        this._comp = comp;

        this._mustFlush = true;
        this._premultipliedAlpha = comp.premultipliedAlpha;
        this._multiplier = 1.0;
        this._handleVal = 0x00;
        this._needColor = false;
        this._vertexEffect = comp._effectDelegate && comp._effectDelegate.this._vertexEffect;

        if (nodeColor._val !== 0xffffffff || this._premultipliedAlpha) {
            this._needColor = true;
        }

        if (this._useTint) {
            this._handleVal |= FLAG_TWO_COLOR;
        }

        let worldMat = undefined;
        if (this._comp.enableBatch) {
            worldMat = this._node._worldMatrix;
            this._mustFlush = false;
            this._handleVal |= FLAG_BATCH;
        }

        if (comp.isAnimationCached()) {
            // Traverse input assembler.
            this.cacheTraverse(worldMat);
        } else {
            if (this._vertexEffect) this._vertexEffect.begin(comp._skeleton);
            this.realTimeTraverse(worldMat);
            if (this._vertexEffect) this._vertexEffect.end();
        }

        // sync attached node matrix
        renderer.worldMatDirty++;
        comp.attachUtil._syncAttachedNode();

        // Clear temp var.
        this._node = undefined;
        this._buffer = undefined;
        this._renderer = undefined;
        this._comp = undefined;
        this._vertexEffect = null;
    }

    postFillBuffers(comp, renderer) {
        renderer.worldMatDirty--;
    }
}

cc.Assembler.register(sp.Skeleton, SpineAssembler);
